import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import Link from 'next/link';

import analytics from '@repo/analytics';
import { getElementAuthor } from '@repo/editor/src/components/Profile/utils';
import {
  AUTHENTICATION_CLICK_SIGNUP,
  TEMPLATE_AUTHOR_CLICK,
} from '@repo/editor/src/global/analytics/events';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { ArtboardOptions } from '@repo/editor/src/types';
import { EventCatcherScope } from '@repo/event-processor';
import { IconButton } from '@repo/ui/components/Buttons';
import CopyIcon from '@repo/ui/components/Icon/CopyIcon';
import FilledHeartIcon from '@repo/ui/components/Icon/FilledHeartIcon';
import HeartIcon from '@repo/ui/components/Icon/HeartIcon';
import LikesIcon from '@repo/ui/components/Icon/LikesIcon';
import PremiumBadgeOutlinedIcon from '@repo/ui/components/Icon/PremiumBadgeOutlinedIcon';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { themeStyle } from '@repo/ui/theming';
import { Tooltip } from '@repo/ui-kit/components/core/Tooltip';
import { H6, P4 } from '@repo/ui-kit/components/core/Typography';

import usePostUpdate from 'components/hooks/usePostUpdate';
import TopActions from 'components/PostPreview/TopActions';
import TopLabels from 'components/PostPreview/TopLabels';
import { PostPreviewProps } from 'components/PostPreview/types';
import { getSrcSetUrl, srcSetToImage } from 'components/utilities/srcSetUtils';
import {
  useDashboardTileImprovements,
  useShowLikeIcon,
} from 'global/growthbookUtils';
import { DesignPostData } from 'global/types/DesignPostData';
import {
  buildPostPath,
  buildEditorUrlWithTemplateParams,
  getTemplateSEOName,
  getTemplateSEOObjectName,
  buildPublicProfilePath,
} from 'global/urlUtils';
import { openInNewTabWithFallback } from 'global/utils/browserUtils';
import useSearch from 'global/utils/useSearch';
import useWindowSize from 'global/utils/useWindowSize';
import { useActiveWorkspaceId } from 'global/utils/workspace/useActiveWorkspaceId';
import {
  mobileRoutingStoreSelector,
  useMobileRoutingStore,
} from 'stores/mobileRoutingStore';
import { postsStoreSelector, usePostsStore } from 'stores/postsStore';
import { FlexRow } from 'styles/layouts';
import { HideMobile } from 'styles/queries';

import {
  BorderBox,
  BottomRow,
  CategoryLabel,
  FlexGroup,
  Image,
  ImageWrapper,
  InfoOnHover,
  LikesAndPopularityFlexGroup,
  PostTitle,
  PremiumIconWrapper,
  StyledArtboardCountBadge,
  TemplateInfo,
  TopButton,
  AnchorWrapperWithEventCatcher,
} from './styles';
import { likeIconTheme } from './theme';
// PostPreview is usually used in a masonry layout, therefore we more likely
// to have tall and narrow images, setting height to 0, allows us to just fit width
export const POST_PREVIEW_IMAGE_WIDTH = 350;
export const POST_PREVIEW_IMAGE_HEIGHT = 0;

/**
 * Simple component to preview a post of a template
 */
const PostPreview = forwardRef<HTMLAnchorElement, PostPreviewProps>(
  (
    {
      compact = false,
      loading = 'lazy',
      clickAction = 'postDetails',
      areLikesAndPopularityHiddenOnMobile,
      currentUseCase,
      currentSubUseCase,
      featureContext,
      shouldShowAuthorAndPopularity = true,
      ...props
    },
    ref
  ) => {
    const getPost = usePostsStore(postsStoreSelector.getPost);
    const [post, setPost] = useState<DesignPostData>(props.post);
    const [likesCount, setLikesCount] = useState(props.post.likes);
    const [isLikedByBookmark, setIsLikedByBookmark] = useState<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { query } = useSearch();
    const { isMobile } = useWindowSize();
    const isDashboardTileImprovementsEnabled = useDashboardTileImprovements();
    const showAuthorAndPopularity =
      (!isMobile || !isDashboardTileImprovementsEnabled) &&
      shouldShowAuthorAndPopularity;

    const isShowLikeIconEnabled = useShowLikeIcon();
    const openAuthentication = useAuthenticationStore(
      authenticationStoreSelector.openAuthentication
    );
    const proAccess = useUserStore(userStoreSelector.proAccess);
    const showUpgradeModal = usePromotionStore(
      promotionSelector.showUpgradeModal
    );
    const tryRoutingMobileNavigation = useMobileRoutingStore(
      mobileRoutingStoreSelector.tryRouting
    );
    const user = useUserStore(userStoreSelector.user);
    usePostUpdate(props.post.id, setPost);

    const canUserCreateDesign = useDesignsStore(
      designsStoreSelector.canUserCreateDesign
    );

    const workspaceId = useActiveWorkspaceId();

    const editorUrlWithTemplateParams = buildEditorUrlWithTemplateParams({
      id: props.post.template?.id.toString(),
      useCase: currentUseCase,
      subUseCase: currentSubUseCase,
      query,
      feedIndex: props.post.feedIndex
        ? props.post.feedIndex.toString()
        : undefined,
      featureOrigin: featureContext,
      workspaceId,
    });

    const scopeParams = useMemo(() => {
      return {
        templateId: props.post.template.id,
        templateName: props.post.template.name,
        pageName: featureContext,
        isPremium: props.post.template.premium,
        rankingScore: props.post.template.rankingScore,
        feedIndex: props.post.feedIndex,
      };
    }, [
      props.post.template.id,
      props.post.template.name,
      featureContext,
      props.post.template.premium,
      props.post.template.rankingScore,
      props.post.feedIndex,
    ]);

    const handleAuthorLink = () => {
      analytics.track(TEMPLATE_AUTHOR_CLICK, {
        label: author?.name || 'unknown',
      });
    };

    useEffect(() => {
      // update post data with data from store if it exists
      const storePost = getPost(props.post.id);
      if (storePost) {
        setPost(storePost);
      }
    }, [props.post.id, getPost]);

    const author = props.post.template?.createdBy
      ? getElementAuthor({ createdBy: props.post.template.createdBy })
      : null;

    const redirectToAuthentication = (): void => {
      void analytics.track(AUTHENTICATION_CLICK_SIGNUP, { label: 'useDesign' });
      openAuthentication();
    };

    const showPremiumUpgradeModal = (): void => {
      showUpgradeModal('premiumDesignUsage', {
        designPreview: post.template?.templatePreviewObjectName,
        data: {
          templateId: post.template?.id,
        },
      });
    };

    const checkIfUserCanCreateDesign = () => {
      canUserCreateDesign({
        canShowProjectListModal: true,
        redirectUrl: editorUrlWithTemplateParams,
        cb: () => window.open(editorUrlWithTemplateParams, '_blank'),
      });
    };

    const routeMobileNavigation = (): void => {
      tryRoutingMobileNavigation(checkIfUserCanCreateDesign);
    };

    const canOpenEditorAction = (): boolean => {
      if (!user) {
        redirectToAuthentication();
        return false;
      }

      if (post.template?.premium && !proAccess) {
        showPremiumUpgradeModal();
        return false;
      }

      if (isMobile) {
        routeMobileNavigation();
        return false;
      }

      return true;
    };

    const likePost = usePostsStore(postsStoreSelector.likePost);

    const handleLikePost = (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      setIsLikedByBookmark(false);

      likePost(post.id);
    };

    useEffect(() => {
      if (post.likeStatus === 'resolved') {
        // if user unliked the post or liked the post but did so via Bookmark, no delay
        if (!post.likedByUser || isLikedByBookmark) {
          setLikesCount(post.likes);
          return;
        }
        // otherwise, 2 sec delay to increase the number of likes
        timeoutRef.current = setTimeout(() => {
          setLikesCount(post.likes);
        }, 2000);
      }
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [post.likeStatus, post.likedByUser, post.likes, isLikedByBookmark]);

    const openTemplateDetails = (): void => {
      const handleClickFunction =
        clickAction === 'openEditor' && props.onPostDetailsClick
          ? props.onPostDetailsClick
          : props.onClick;
      handleClickFunction?.({
        ...props.post,
        likedByUser: post.likedByUser,
        likes: post.likes,
      });
      const postPath = buildPostPath(props.post);
      const newState = {
        ...window.history.state,
        url: postPath,
        as: postPath,
      };
      window.history.pushState(newState, '', postPath);
    };

    const handleLikeTopActions = () => {
      setIsLikedByBookmark(true);
    };

    const handleOnClick = (event: React.MouseEvent): void => {
      // meta / ctrl click opens a new tab
      if (props.onClick && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        props.onClick?.({
          ...post,
          likedByUser: post.likedByUser,
          likes: post.likes,
        });

        if (clickAction === 'openEditor') {
          canOpenEditorAction() &&
            canUserCreateDesign({
              canShowProjectListModal: true,
              redirectUrl: editorUrlWithTemplateParams,
              cb: () => {
                openInNewTabWithFallback(editorUrlWithTemplateParams);
              },
            });
        } else {
          openTemplateDetails();
        }
      }
    };

    return (
      <EventCatcherScope module="templateCard" scopeParameters={scopeParams}>
        <AnchorWrapperWithEventCatcher
          ref={ref}
          data-testid="design-posts--preview"
          href={buildPostPath(props.post)}
          name="templateCard"
          onClick={handleOnClick}
        >
          <ImageWrapper
            // @ts-expect-error FixMe color issue
            background={
              (
                props.post.template?.state as {
                  artboardOptions?: ArtboardOptions;
                }
              )?.artboardOptions?.fill ?? themeStyle.transparent
            }
          >
            <Image
              alt={
                props.post.template
                  ? getTemplateSEOName(props.post.template)
                  : 'template image'
              }
              height={props.post.elementHeight}
              loading={loading}
              srcSet={srcSetToImage(
                getSrcSetUrl(post.template?.templatePreviewObjectName ?? '', {
                  seoObjectName:
                    getTemplateSEOObjectName(post.template) ?? undefined,
                  width: POST_PREVIEW_IMAGE_WIDTH,
                  height: POST_PREVIEW_IMAGE_HEIGHT,
                })
              )}
              width={props.post.elementWidth}
            />
            <HideMobile>
              <TopButton
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <TopActions
                  canEdit={!!props.canEdit}
                  canLike={!!props.canLike}
                  isOptionList={!!props.useOptionsList}
                  liked={post.likedByUser}
                  post={props.post}
                  onDelete={props.onDelete}
                  onDetailsClick={
                    props.onPostDetailsClick ? openTemplateDetails : undefined
                  }
                  onLike={handleLikeTopActions}
                />
              </TopButton>
            </HideMobile>
            {props.post.template?.id && (
              <TopLabels
                challenge={props.post.challenge}
                challengePosition={props.post.challengePosition}
                templateId={props.post.template.id}
              />
            )}
            <TemplateInfo>
              <InfoOnHover>
                <CategoryLabel color={themeStyle.inkBase}>
                  {props.post.template?.groups[0]?.category?.name}
                </CategoryLabel>
              </InfoOnHover>
              <FlexRow alignItems="center" gap="9px">
                {props.post.template?.premium && (
                  <PremiumIconWrapper>
                    <PremiumBadgeOutlinedIcon height="26px" />
                  </PremiumIconWrapper>
                )}
                <InfoOnHover>
                  <PostTitle noTextOverflow color={themeStyle.inkBase}>
                    {props.post.template?.name}
                  </PostTitle>
                </InfoOnHover>
              </FlexRow>
            </TemplateInfo>
            <BorderBox />

            {props.post.template.numberOfArtboards &&
              props.post.template.numberOfArtboards > 1 && (
                <StyledArtboardCountBadge
                  count={props.post.template.numberOfArtboards}
                />
              )}
          </ImageWrapper>
        </AnchorWrapperWithEventCatcher>
        {showAuthorAndPopularity && (
          <FlexRow
            alignItems="center"
            gap={compact ? '4px' : 'unset'}
            justifyContent="space-between"
            marginTop="8px"
          >
            <Link
              href={buildPublicProfilePath(props.post.template.createdBy?.slug)}
              onClick={handleAuthorLink}
            >
              <FlexGroup>
                <ProfileImage
                  profile={{
                    name: author?.name,
                    profileImage: author?.profileImage,
                    isTeam: false,
                  }}
                  size={compact ? '20px' : '25px'}
                />
                <H6 margin="0 0 0 6px" textOverflowEllipsis={compact}>
                  {author?.name}
                </H6>
              </FlexGroup>
            </Link>
            <BottomRow>
              <LikesAndPopularityFlexGroup
                areLikesAndPopularityHiddenOnMobile={
                  areLikesAndPopularityHiddenOnMobile
                }
              >
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <FlexGroup>
                      {isShowLikeIconEnabled ? (
                        <IconButton
                          ariaLabel="like"
                          eventOptions={{
                            action: post.likedByUser ? 'removeLike' : 'addLike',
                          }}
                          height="12px"
                          icon={post.likedByUser ? FilledHeartIcon : HeartIcon}
                          name="likeButton"
                          theme={likeIconTheme}
                          onClick={handleLikePost}
                        />
                      ) : (
                        <LikesIcon
                          height="12px"
                          theme={{ color: themeStyle.varInkMedium }}
                        />
                      )}
                      <P4 color="secondary" margin="0 10px 0 5px">
                        {likesCount}
                      </P4>
                    </FlexGroup>
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    <H6 as="span" color="brand-on-bg">
                      Likes
                    </H6>
                  </Tooltip.Content>
                </Tooltip.Root>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <FlexGroup>
                      <CopyIcon
                        height="12px"
                        theme={{ color: themeStyle.varInkMedium }}
                      />
                      <P4 color="secondary" margin="0 0 0 5px">
                        {props.post.template?.popularity}
                      </P4>
                    </FlexGroup>
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    <H6 as="span" color="brand-on-bg">
                      Popularity
                    </H6>
                  </Tooltip.Content>
                </Tooltip.Root>
              </LikesAndPopularityFlexGroup>
            </BottomRow>
          </FlexRow>
        )}
      </EventCatcherScope>
    );
  }
);

PostPreview.displayName = 'PostPreview';

const PostPreviewMemoized = React.memo(PostPreview, (prevProps, newProps) => {
  return (
    // post.id shouldn't really change, but if it does we need to re-render
    prevProps.post.id === newProps.post.id &&
    prevProps.post.feedIndex === newProps.post.feedIndex
  );
});

PostPreviewMemoized.displayName = 'PostPreview';

export default PostPreviewMemoized;
